/*
 * Multi-Ship page: one purchase, many recipients.
 *
 * Reads the cart, lets the buyer split its bottles across recipients (typed,
 * saved addresses, or a CSV for corporate lists), prices every recipient
 * through cockpit (Shopify's own rates, free-shipping rules and tax), and
 * hands back ONE Shopify checkout. After payment cockpit splits the order into
 * one real order per recipient. Backend: ~/projects/cockpit/lib/multiship/.
 *
 * Vanilla custom element, no dependencies. State lives in localStorage so a
 * reload, or coming back from an abandoned checkout, loses nothing.
 */
(() => {
  if (customElements.get('multi-ship')) return;

  const STORE_KEY = 'ms-state-v1';
  const RESUME_TTL_MS = 72 * 3600 * 1000; // cockpit deletes unpaid drafts after 72 h
  const MAX_RECIPIENTS = 500;
  const MAX_QTY = 48;
  const MAX_ITEMS = 24;
  const CARD_LIMIT = 12; // above this, recipients collapse into rows
  // Gift wrap: Giftship's colours for the Extras GIFTWRAP line. cockpit adds one wrap per bottle in
  // the recipient's box and writes the colour as the line's `color` property, as a product-page wrap does.
  const WRAP_COLORS = ['Gold', 'Silver', 'Red'];
  // Street suggestions: Mapbox Address Autofill, the product its terms name for address forms that get saved.
  // The token is Mapbox's public kind, made for page code. The account has no card on file, so a copied token
  // can only use up the free tier (1,000 addresses a month), which turns suggestions off, never bills.
  const AUTOFILL = 'https://api.mapbox.com/autofill/v1';
  const sessionId = () => (crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const n = (Math.random() * 16) | 0;
    return (c === 'x' ? n : (n & 3) | 8).toString(16);
  }));
  const stateOf = (a) => String(a?.region_code || a?.address_level1 || '').toUpperCase();
  // Line icons in the brand's icon-set style (single weight, round ends).
  const svg = (body, cls = 'ms-ico') => `<svg class="${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;
  const ICON = {
    gift: svg('<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8M12 8v13M12 8S10.5 3.5 8 4.5 9 8 12 8zm0 0s1.5-4.5 4-3.5S15 8 12 8z"/>'),
    note: svg('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3.5 6.5 8.5 6.5 8.5-6.5"/>'),
    box: svg('<path d="M21 8 12 3 3 8v8l9 5 9-5z"/><path d="m3 8 9 5 9-5M12 13v8"/>'),
    lock: svg('<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>'),
  };
  // The Liquor Geeks glasses mark: rings in currentColor, lenses half full of brand yellow.
  const GLASSES = `<svg class="ms-glasses" viewBox="0 0 64 30" aria-hidden="true"><g class="ms-glasses__lens"><circle cx="14" cy="15" r="9"/><circle cx="50" cy="15" r="9"/></g><g fill="#fabc0a"><path d="M6.2 16q2-2 4 0t4 0 4 0 3.6 0A8.1 8.1 0 0 1 6.2 16z"/><path d="M42.2 16q2-2 4 0t4 0 4 0 3.6 0A8.1 8.1 0 0 1 42.2 16z"/></g><g fill="none" stroke="currentColor" stroke-width="4"><circle cx="14" cy="15" r="10"/><circle cx="50" cy="15" r="10"/><path d="M24 13q8-5 16 0"/></g></svg>`;
  const STATES = [
    ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'], ['CO', 'Colorado'],
    ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'],
    ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
    ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'],
    ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'],
    ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
    ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
    ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'],
    ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
    ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
  ];
  const STATE_BY_NAME = Object.fromEntries(STATES.map(([c, n]) => [n.toLowerCase().replace(/[^a-z]/g, ''), c]));
  const STATE_CODES = new Set(STATES.map(([c]) => c));
  const ADDRESS_FIELDS = ['firstName', 'lastName', 'company', 'address1', 'address2', 'city', 'provinceCode', 'zip', 'phone'];

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const money = (c) => `${c < 0 ? '\u2212' : ''}$${(Math.abs(c) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
  const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many || `${one}s`}`;
  // "a", "a and b", "a, b, and c": the missing-field lists read as a sentence.
  const listJoin = (a) => (a.length < 3 ? a.join(' and ') : `${a.slice(0, -1).join(', ')}, and ${a[a.length - 1]}`);
  // Only ever send the buyer to a Shopify https checkout link we were handed.
  const safeUrl = (u) => /^https:\/\/[^\s"'<>]+$/.test(String(u || ''));
  const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(e || '').trim());

  const blankRecipient = () => ({
    key: uid(), firstName: '', lastName: '', company: '', address1: '', address2: '', city: '', provinceCode: '', zip: '', phone: '',
    giftTo: '', giftFrom: '', giftMessage: '', giftWrap: '', rateTitle: '', items: {},
  });
  const units = (r) => Object.values(r.items || {}).reduce((a, q) => a + (q > 0 ? q : 0), 0);
  const isBlank = (r) => !ADDRESS_FIELDS.some((f) => String(r[f] || '').trim()) && units(r) === 0;
  const missing = (r) => {
    const m = [];
    if (!r.firstName.trim() || !r.lastName.trim()) m.push('name');
    if (r.address1.trim().length < 3) m.push('street address');
    if (!r.city.trim()) m.push('city');
    if (!STATE_CODES.has(r.provinceCode)) m.push('state');
    if (!/^\d{5}(-\d{4})?$/.test(r.zip.trim())) m.push('5-digit ZIP');
    if (units(r) === 0) m.push('a bottle');
    return m;
  };
  const complete = (r) => missing(r).length === 0;

  // -- CSV ------------------------------------------------------------------
  function parseCSV(text) {
    text = text.replace(/^\uFEFF/, '');
    const firstLine = text.split(/\r?\n/, 1)[0] || '';
    const delim = !firstLine.includes(',') && firstLine.includes('\t') ? '\t' : !firstLine.includes(',') && firstLine.includes(';') ? ';' : ',';
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (quoted) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
        } else field += c;
      } else if (c === '"') quoted = true;
      else if (c === delim) { row.push(field); field = ''; }
      else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') i++;
        row.push(field); rows.push(row); row = []; field = '';
      } else field += c;
    }
    if (field !== '' || row.length) { row.push(field); rows.push(row); }
    return rows.filter((r) => r.some((v) => v.trim() !== ''));
  }
  const csvCell = (v) => (/[",\r\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  const FIELD_ALIASES = {
    firstName: ['firstname', 'first', 'fname', 'givenname', 'recipientfirstname'],
    lastName: ['lastname', 'last', 'lname', 'surname', 'familyname', 'recipientlastname'],
    name: ['name', 'fullname', 'recipient', 'recipientname', 'shiptoname'],
    company: ['company', 'companyname', 'organization', 'organisation', 'business'],
    address1: ['address1', 'address', 'addressline1', 'street', 'streetaddress', 'address1street', 'shippingaddress'],
    address2: ['address2', 'addressline2', 'apt', 'suite', 'unit', 'aptsuite', 'aptsuiteunit'],
    city: ['city', 'town'],
    provinceCode: ['state', 'province', 'st', 'statecode', 'region', 'stateprovince'],
    zip: ['zip', 'zipcode', 'postalcode', 'postcode', 'zip5', 'postal'],
    phone: ['phone', 'phonenumber', 'tel', 'telephone', 'mobile', 'cell'],
    giftTo: ['giftto', 'to', 'noteto'],
    giftFrom: ['giftfrom', 'from', 'notefrom'],
    giftMessage: ['giftmessage', 'message', 'note', 'giftnote', 'card', 'cardmessage'],
    giftWrap: ['giftwrap', 'wrap', 'wrapping', 'giftwrapping', 'wrapcolor', 'wrapcolour', 'giftwrapcolor', 'giftwrapcolour'],
    quantity: ['quantity', 'qty', 'bottles', 'count'],
    sku: ['sku', 'item', 'product', 'bottle'],
  };
  const cleanZip = (z) => {
    const d = String(z || '').replace(/[^\d]/g, '');
    if (d.length === 4) return `0${d}`; // spreadsheets drop the leading zero (NJ, MA, CT\u2026)
    if (d.length === 9) return `${d.slice(0, 5)}-${d.slice(5)}`;
    if (d.length === 8) return `0${d.slice(0, 4)}-${d.slice(4)}`;
    return d.length === 5 ? d : String(z || '').trim();
  };
  // "1", "1.0", "2.00", " 3 " -> whole bottles; Excel and Numbers export decimals.
  const qtyOf = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[^\d.]/g, ''));
    return Number.isFinite(n) ? Math.round(n) : 0;
  };
  // CSV gift wrap cell: a colour name picks that colour; yes/y/x/1/true wraps in the first colour.
  const wrapOf = (v) => {
    const n = norm(v);
    if (!n) return '';
    const c = WRAP_COLORS.find((w) => norm(w) === n);
    if (c) return c;
    return /^(yes|y|x|1|true|wrap|wrapped)$/.test(n) ? WRAP_COLORS[0] : '';
  };
  const cleanState = (s) => {
    const t = String(s || '').trim();
    if (STATE_CODES.has(t.toUpperCase())) return t.toUpperCase();
    return STATE_BY_NAME[t.toLowerCase().replace(/[^a-z]/g, '')] || '';
  };

  class MultiShip extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;
      this.endpoint = (this.dataset.endpoint || '').replace(/\/$/, '');
      this.shop = this.dataset.shop || 'lg';
      this.support = this.dataset.support || '';
      // Per-bottle price of the store's GIFTWRAP variant, from Liquid; 0 hides the option.
      this.wrapPrice = parseInt(this.dataset.wrapPrice, 10) || 0;
      this.acToken = this.dataset.addressToken || '';
      this.ac = null; // open suggestion list: { input, items, active }
      this.cartWrap = '';
      try {
        this.customer = JSON.parse(this.querySelector('[data-ms-customer]')?.textContent || 'null');
      } catch {
        this.customer = null;
      }
      this.quote = null; // last good quote response
      this.quoteSig = ''; // request body the quote answers
      this.quoteError = '';
      this.payError = '';
      this.notice = '';
      this.loading = false;
      this.seq = 0;
      this.expanded = null; // recipient key open in row mode
      this.boot();
    }

    // -- state --------------------------------------------------------------
    load() {
      try {
        const s = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
        return s && s.v === 1 ? s : null;
      } catch {
        return null;
      }
    }

    save() {
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify(this.state));
      } catch {
        /* private mode / quota: the page still works, it just won't survive a reload */
      }
    }

    cartItems(cart) {
      const byVariant = new Map();
      const skipped = [];
      for (const it of cart.items || []) {
        const props = it.properties || {};
        const sku = String(it.sku || '').toUpperCase();
        if (sku === 'GIFTNOTE') continue; // replaced by the per-recipient gift note below
        if (/^GIFTWRAP/.test(sku)) {
          // A wrap added on a product page: offered per recipient below instead, in the same colour.
          const c = String(props.color || props.Color || '');
          this.cartWrap = WRAP_COLORS.find((w) => w.toLowerCase() === c.toLowerCase()) || this.cartWrap || WRAP_COLORS[0];
          continue;
        }
        if (/navidium|shipping-protection/i.test(it.handle || '') || /shipping protection/i.test(it.product_title || '')) continue;
        if (Object.keys(props).some((k) => k.startsWith('_gs_bundle'))) {
          skipped.push(`${it.product_title} (bundle)`);
          continue;
        }
        if (it.requires_shipping === false) continue;
        const id = String(it.variant_id);
        const prev = byVariant.get(id);
        if (prev) {
          prev.cartQty += it.quantity;
          continue;
        }
        const vt = it.variant_title && it.variant_title !== 'Default Title' ? ` (${it.variant_title})` : '';
        byVariant.set(id, {
          variantId: id,
          title: `${it.product_title}${vt}`,
          sku: it.sku || '',
          image: it.featured_image?.url || it.image || '',
          priceCents: it.original_price ?? it.price,
          cartQty: it.quantity,
        });
      }
      return { items: [...byVariant.values()], skipped };
    }

    async boot() {
      let cart = { items: [] };
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        cart = await res.json();
      } catch {
        /* treat as empty */
      }
      const { items, skipped } = this.cartItems(cart);
      this.skipped = skipped;
      const saved = this.load();

      const pending = saved?.redirect && Date.now() - saved.redirect.at < RESUME_TTL_MS && safeUrl(saved.redirect.invoiceUrl) ? saved.redirect : null;
      if (pending && !items.length) {
        this.state = saved;
        this.cartNow = items;
        this.renderResume();
        return;
      }
      if (pending) {
        // Shopping again: most likely they paid. Offer the old checkout once, as a note, and move on.
        this.notice = `<p>You have an unfinished multi-address checkout from earlier. <a class="link" href="${esc(pending.invoiceUrl)}">Return to it</a>, or carry on below to build a new one. If you already paid, you can ignore this.</p>`;
        delete saved.redirect;
      }
      if (!items.length) {
        this.renderEmpty();
        return;
      }
      this.startFrom(items, saved);
    }

    startFrom(items, saved) {
      const ids = new Set(items.map((i) => i.variantId));
      let recipients = (saved?.recipients || []).map((r) => ({
        ...blankRecipient(),
        ...r,
        items: Object.fromEntries(Object.entries(r.items || {}).filter(([v, q]) => ids.has(v) && q > 0)),
      }));
      recipients = recipients.filter((r) => !isBlank(r));
      while (recipients.length < 2) recipients.push(this.newRecipient(items, recipients.length));
      if (this.wrapPrice && this.cartWrap && !saved?.recipients?.length && !this.notice) {
        this.notice = `<p>Gift wrap from your cart is now chosen per recipient: each bottle is wrapped in ${esc(this.cartWrap.toLowerCase())}. Change or turn it off on any recipient below.</p>`;
      }
      this.state = {
        v: 1,
        items,
        recipients,
        email: saved?.email || this.customer?.email || '',
        code: saved?.code || '',
      };
      this.save();
      this.render();
      this.scheduleQuote(0);
    }

    newRecipient(items = this.state.items, index = this.state?.recipients.length || 0) {
      const r = blankRecipient();
      if (this.wrapPrice && this.cartWrap) r.giftWrap = this.cartWrap;
      // One kind of bottle in the cart: start each new recipient on one of it.
      if (items.length === 1 && index < Math.max(items[0].cartQty, 2)) r.items[items[0].variantId] = 1;
      return r;
    }

    // -- rendering ----------------------------------------------------------
    renderEmpty() {
      const onlyBundles = this.skipped?.length;
      this.innerHTML = `
        <div class="ms-empty">
          <div class="ms-empty__art">${GLASSES}</div>
          <h2 class="ms-display ms-h">${onlyBundles ? 'Nothing here can ship to multiple addresses' : 'Your cart\u2019s looking a little dry'}</h2>
          <p>${onlyBundles
            ? `${esc(this.skipped.join(', '))} ${this.skipped.length === 1 ? 'ships' : 'ship'} to one address, so use the normal checkout for ${this.skipped.length === 1 ? 'it' : 'them'}. Add other bottles to send them to several people.`
            : 'Add the bottles you want to send, then come back here to choose who gets what.'}</p>
          <a class="btn btn--primary" href="${esc(this.dataset.shopUrl || '/collections/all')}">Shop bottles</a>
        </div>`;
    }

    renderResume() {
      const r = this.state.redirect;
      if (!safeUrl(r?.invoiceUrl)) {
        this.renderEmpty();
        return;
      }
      const n = this.state.recipients?.length || 0;
      this.innerHTML = `
        <div class="ms-empty">
          <div class="ms-empty__art">${GLASSES}</div>
          <h2 class="ms-display ms-h">Your checkout is waiting</h2>
          <p>You started paying for an order to ${plural(n, 'recipient')}. If you already paid, check your email for the confirmation and you're all set.</p>
          <div class="ms-actions">
            <a class="btn btn--primary" href="${esc(r.invoiceUrl)}">Return to payment</a>
            <button type="button" class="btn btn--secondary" data-act="restart">Start a new multi-address order</button>
          </div>
        </div>`;
      this.querySelector('[data-act="restart"]').addEventListener('click', () => {
        const keep = { ...this.state, redirect: undefined };
        delete keep.redirect;
        if (this.cartNow?.length) this.startFrom(this.cartNow, keep);
        else {
          try {
            localStorage.setItem(STORE_KEY, JSON.stringify({ ...keep, items: [] }));
          } catch {
            /* ignore */
          }
          this.renderEmpty();
        }
      });
    }

    render() {
      const s = this.state;
      const rowMode = s.recipients.length > CARD_LIMIT;
      this.innerHTML = `
        <div class="ms">
          <div class="ms-main">
            <section class="ms-panel ms-pool" aria-labelledby="ms-pool-h">
              <h2 class="ms-display ms-h" id="ms-pool-h">Your bottles</h2>
              <div data-region="pool">${this.poolHTML()}</div>
            </section>

            <section class="ms-recipients" aria-labelledby="ms-rec-h">
              <div class="ms-rec-head">
                <h2 class="ms-display ms-h" id="ms-rec-h">Who gets what <span class="ms-count" data-region="count">${s.recipients.length}</span></h2>
                <div class="ms-actions">
                  <button type="button" class="btn btn--secondary btn--narrow" data-act="add">Add recipient</button>
                  <button type="button" class="btn btn--secondary btn--narrow" data-act="csv">Upload a list (CSV)</button>
                  <button type="button" class="link ms-link" data-act="template">CSV template</button>
                  <input type="file" accept=".csv,text/csv,.txt,text/plain,text/tab-separated-values" data-csv hidden>
                </div>
              </div>
              <div class="ms-notice" data-region="notice" role="status" aria-live="polite">${this.noticeHTML()}</div>
              <div class="ms-list${rowMode ? ' ms-list--rows' : ''}" data-region="list">
                ${s.recipients.map((r, i) => (rowMode && this.expanded !== r.key ? this.rowHTML(r, i) : this.cardHTML(r, i))).join('')}
              </div>
              <button type="button" class="btn btn--secondary w-full ms-add-bottom" data-act="add">+ Add another recipient</button>
            </section>
          </div>

          <aside class="ms-summary" aria-labelledby="ms-sum-h">
            <div class="ms-summary__head">
              <h2 class="ms-display ms-h" id="ms-sum-h">Your order</h2>
              ${GLASSES}
            </div>
            <div class="ms-summary__body">
            <div data-region="summary" aria-live="polite">${this.summaryHTML()}</div>
            <div class="ms-field">
              <label class="label" for="ms-code">Discount code</label>
              <div class="ms-inline">
                <input class="input w-full" id="ms-code" data-g="code" value="${esc(s.code)}" autocomplete="off" autocapitalize="characters" placeholder="Code">
                <button type="button" class="btn btn--secondary btn--narrow" data-act="apply-code">Apply</button>
              </div>
            </div>
            <div class="ms-field">
              <label class="label" for="ms-email">Your email</label>
              <input class="input w-full" id="ms-email" type="email" data-g="email" value="${esc(s.email)}" autocomplete="email" placeholder="For your receipt and tracking">
            </div>
            <div data-region="pay">${this.payHTML()}</div>
            <ul class="ms-trust" role="list">
              <li>${ICON.box}<span>Every box ships on its own, with its own tracking. On orders to 12 or fewer people, we email you each tracking number as it ships.</span></li>
              <li>${ICON.lock}<span>You pay once, on Shopify's secure checkout.</span></li>
              <li>${ICON.note}<span>Add a free gift note to any box.</span></li>
            </ul>
            </div>
          </aside>
        </div>
        <div class="ms-bar" data-region="bar">${this.barHTML()}</div>`;
      this.bind();
      this.syncBar();
    }

    // The phone bar is a shortcut to the summary. Hide it once the summary (with its own pay button) is on
    // screen, so it never covers the summary or the footer. While it shows, html.ms-bar-on lifts the Joy loyalty
    // and Gorgias chat buttons above it (multi-ship.css), which otherwise sat on the bar's own button.
    syncBar() {
      const bar = this.querySelector('.ms-bar');
      const sum = this.querySelector('.ms-summary');
      const on = !!(bar && sum) && window.matchMedia('(max-width: 989px)').matches && sum.getBoundingClientRect().top > window.innerHeight - 80;
      if (bar) bar.classList.toggle('is-off', !on);
      document.documentElement.classList.toggle('ms-bar-on', on);
    }

    poolHTML() {
      const s = this.state;
      const assigned = this.assignedByVariant();
      const items = s.items.map((it) => {
        const a = assigned.get(it.variantId) || 0;
        const diff = it.cartQty - a;
        const status = diff > 0
          ? `<span class="ms-warn">${diff} not assigned yet</span>`
          : diff < 0
            ? `<span class="ms-muted">${-diff} more than your cart</span>`
            : '<span class="ms-ok">All assigned</span>';
        return `
          <li class="ms-pool__item">
            ${it.image ? `<img src="${esc(`${it.image}${it.image.includes('?') ? '&' : '?'}width=120`)}" alt="" width="56" height="56" loading="lazy">` : '<span class="ms-pool__ph"></span>'}
            <div class="ms-pool__body">
              <p class="ms-pool__title">${esc(it.title)}</p>
              <p class="ms-small">${money(it.priceCents)} \u00b7 in cart ${it.cartQty} \u00b7 assigned ${a} \u00b7 ${status}</p>
            </div>
            <button type="button" class="link ms-link" data-act="all1" data-v="${esc(it.variantId)}" title="Give every recipient one of this">1 to each</button>
          </li>`;
      });
      const skipped = this.skipped?.length
        ? `<p class="ms-small ms-muted">Not included: ${this.skipped.map(esc).join(', ')}. Bundles ship to one address; check those out separately.</p>`
        : '';
      const total = s.items.reduce((a, it) => a + it.cartQty, 0);
      const done = s.items.reduce((a, it) => a + Math.min(it.cartQty, assigned.get(it.variantId) || 0), 0);
      const pct = total ? Math.round((done / total) * 100) : 0;
      const meter = `
        <div class="ms-meter${total && done >= total ? ' is-done' : ''}">
          <div class="ms-meter__label"><strong>${done} of ${plural(total, 'bottle')} assigned</strong>${total && done >= total ? '<span>All set \u2713</span>' : ''}</div>
          <div class="ms-meter__track" role="progressbar" aria-label="Bottles assigned" aria-valuemin="0" aria-valuemax="${total}" aria-valuenow="${done}"><span style="width:${pct}%"></span></div>
        </div>`;
      return `${meter}<ul class="ms-pool__list" role="list">${items.join('')}</ul>${skipped}
        <p class="ms-small ms-muted">Your order uses the quantities you give each recipient below. Need more of something? Raise it on the recipient.</p>`;
    }

    savedAddressSelect(r) {
      const list = this.customer?.addresses || [];
      if (!list.length) return '';
      return `
        <div class="ms-field">
          <label class="label" for="ms-saved-${esc(r.key)}">Use a saved address</label>
          <div class="select relative">
            <select class="w-full" id="ms-saved-${esc(r.key)}" data-saved="${esc(r.key)}">
              <option value="">Choose\u2026</option>
              ${list.map((a, i) => `<option value="${i}">${esc([`${a.firstName || ''} ${a.lastName || ''}`.trim(), a.address1, `${a.city || ''} ${a.provinceCode || ''}`.trim()].filter(Boolean).join(', '))}</option>`).join('')}
            </select>
          </div>
        </div>`;
    }

    field(r, f, label, opts = {}) {
      const id = `ms-${f}-${esc(r.key)}`;
      return `
        <div class="ms-field${opts.cls ? ` ${opts.cls}` : ''}">
          <label class="label" for="${id}">${label}${opts.optional ? ' <span class="ms-muted">(optional)</span>' : ''}</label>
          <input class="input w-full" id="${id}" data-k="${esc(r.key)}" data-f="${f}" value="${esc(r[f])}"${opts.attrs || ''}>
        </div>`;
    }

    // Each card is its own <form> and its fields carry a per-card autofill section, so a browser's saved
    // addresses (or iPhone's "AutoFill Contact") fill only the card being edited, never the others.
    cardHTML(r, i) {
      const k = esc(r.key);
      const states = STATES.map(([c, n]) => `<option value="${c}"${r.provinceCode === c ? ' selected' : ''}>${n}</option>`).join('');
      const rowMode = this.state.recipients.length > CARD_LIMIT;
      const ac = (token) => ` autocomplete="section-ms${k} shipping ${token}"`;
      return `
        <form class="ms-card" data-card="${k}" novalidate>
          <header class="ms-card__head">
            ${this.badgeHTML(r, i)}
            ${this.titleHTML(r)}
            <div class="ms-actions">
              ${rowMode ? `<button type="button" class="link ms-link" data-act="collapse" data-k="${k}">Done</button>` : ''}
              <button type="button" class="link ms-link" data-act="remove" data-k="${k}" aria-label="Remove recipient ${i + 1}">Remove</button>
            </div>
          </header>
          ${this.savedAddressSelect(r)}
          <p class="ms-kicker">Ship to</p>
          <div class="ms-grid ms-grid--2">
            ${this.field(r, 'firstName', 'First name', { attrs: ac('given-name') })}
            ${this.field(r, 'lastName', 'Last name', { attrs: ac('family-name') })}
          </div>
          ${this.field(r, 'company', 'Company', { optional: true, attrs: ac('organization') })}
          ${this.field(r, 'address1', 'Street address', { cls: 'ms-field--ac', attrs: ac('address-line1') + (this.acToken ? ` role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="ms-ac-${k}"` : '') })}
          ${this.field(r, 'address2', 'Apt, suite, unit', { optional: true, attrs: ac('address-line2') })}
          <div class="ms-grid ms-grid--3">
            ${this.field(r, 'city', 'City', { attrs: ac('address-level2') })}
            <div class="ms-field">
              <label class="label" for="ms-provinceCode-${k}">State</label>
              <div class="select relative">
                <select class="w-full" id="ms-provinceCode-${k}" data-k="${k}" data-f="provinceCode"${ac('address-level1')}>
                  <option value="">State</option>${states}
                </select>
              </div>
            </div>
            ${this.field(r, 'zip', 'ZIP', { attrs: ` inputmode="numeric" maxlength="10"${ac('postal-code')}` })}
          </div>
          ${this.field(r, 'phone', 'Phone', { optional: true, attrs: ` type="tel"${ac('tel')}` })}

          <div class="ms-items" data-items="${k}">${this.itemsHTML(r)}</div>

          ${this.extrasHTML(r)}

          <div class="ms-quote" data-quote="${k}">${this.quoteHTML(r)}</div>
        </form>`;
    }

    // Number badge; turns into a check once the recipient is priced and shippable.
    badgeHTML(r, i) {
      const q = this.quoteFor(r);
      const ok = q && !q.error && !missing(r).length;
      return `<span class="ms-badge${ok ? ' is-ok' : q?.error ? ' is-err' : ''}" data-badge="${esc(r.key)}" aria-hidden="true">${ok ? '\u2713' : i + 1}</span>`;
    }

    titleHTML(r) {
      const name = `${r.firstName} ${r.lastName}`.trim();
      return `<h3 class="ms-card__title${name ? '' : ' is-empty'}" data-title="${esc(r.key)}">${name ? esc(name) : 'Who\u2019s this one for?'}</h3>`;
    }

    extrasHTML(r) {
      const k = esc(r.key);
      const hasNote = !!(r.giftTo || r.giftFrom || r.giftMessage);
      const open = r.noteOpen ?? hasNote;
      return `
        <div class="ms-extras">
          <p class="ms-kicker">Make it a gift</p>
          <div class="ms-tiles${this.wrapPrice ? '' : ' ms-tiles--one'}">
            ${this.wrapHTML(r)}
            <button type="button" class="ms-tile${open || hasNote ? ' is-on' : ''}" data-act="note" data-k="${k}" aria-expanded="${open}" aria-controls="ms-note-${k}">
              ${ICON.note}
              <span class="ms-tile__text"><strong>Gift note</strong><span data-notestate="${k}">${hasNote ? 'Added' : 'Free'}</span></span>
              <span class="ms-tile__tick" aria-hidden="true"></span>
            </button>
          </div>
          <div data-swatches="${k}">${this.swatchesHTML(r)}</div>
          <div class="ms-note" id="ms-note-${k}" data-notebox="${k}"${open ? '' : ' hidden'}>
            <div class="ms-grid ms-grid--2">
              ${this.field(r, 'giftTo', 'To', { attrs: ' maxlength="60" autocomplete="off"' })}
              ${this.field(r, 'giftFrom', 'From', { attrs: ' maxlength="60" autocomplete="off"' })}
            </div>
            <div class="ms-field">
              <label class="label" for="ms-giftMessage-${k}">Message <span class="ms-muted" data-count="${k}">${r.giftMessage.length}/400</span></label>
              <textarea class="input w-full" rows="3" maxlength="400" id="ms-giftMessage-${k}" data-k="${k}" data-f="giftMessage" placeholder="Cheers to you!">${esc(r.giftMessage)}</textarea>
            </div>
          </div>
        </div>`;
    }

    rowHTML(r, i) {
      const q = this.quoteFor(r);
      const m = missing(r);
      const status = m.length
        ? `<span class="ms-warn">Needs ${esc(listJoin(m))}</span>`
        : q?.error
          ? `<span class="ms-err">${esc(q.error)}</span>`
          : q
            ? `<strong>${money(q.totalCents)}</strong>`
            : '<span class="ms-muted">\u2026</span>';
      const name = `${r.firstName} ${r.lastName}`.trim() || '<span class="ms-muted">No name</span>';
      return `
        <div class="ms-row" data-row="${esc(r.key)}">
          <span class="ms-row__n">${i + 1}</span>
          <span class="ms-row__who"><strong>${r.firstName || r.lastName ? esc(name) : name}</strong><span class="ms-small ms-muted">${esc([r.city, r.provinceCode].filter(Boolean).join(', '))} \u00b7 ${plural(units(r), 'bottle')}${this.wrapPrice && r.giftWrap ? ' \u00b7 gift wrapped' : ''}</span></span>
          <span class="ms-row__status" data-rowstatus="${esc(r.key)}">${status}</span>
          <button type="button" class="link ms-link" data-act="expand" data-k="${esc(r.key)}">Edit</button>
          <button type="button" class="link ms-link" data-act="remove" data-k="${esc(r.key)}" aria-label="Remove recipient ${i + 1}">\u00d7</button>
        </div>`;
    }

    itemsHTML(r) {
      return `
        <p class="label ms-items__label">What they get</p>
        ${this.state.items.map((it) => {
          const q = r.items[it.variantId] || 0;
          return `
            <div class="ms-item${q ? ' is-on' : ''}">
              <span class="ms-item__title">${esc(it.title)}</span>
              <span class="ms-stepper">
                <button type="button" data-act="dec" data-k="${esc(r.key)}" data-v="${esc(it.variantId)}" aria-label="One fewer ${esc(it.title)}"${q ? '' : ' disabled'}>\u2212</button>
                <input type="text" inputmode="numeric" pattern="[0-9]*" data-qty data-k="${esc(r.key)}" data-v="${esc(it.variantId)}" value="${q}" aria-label="Quantity of ${esc(it.title)}">
                <button type="button" data-act="inc" data-k="${esc(r.key)}" data-v="${esc(it.variantId)}" aria-label="One more ${esc(it.title)}"${q >= MAX_QTY ? ' disabled' : ''}>+</button>
              </span>
            </div>`;
        }).join('')}`;
    }

    wrapHTML(r) {
      if (!this.wrapPrice) return '';
      const on = WRAP_COLORS.includes(r.giftWrap);
      const k = esc(r.key);
      return `
        <label class="ms-tile${on ? ' is-on' : ''}" data-wrapbox="${k}">
          <input type="checkbox" class="ms-tile__input" data-wrap="${k}"${on ? ' checked' : ''}>
          ${ICON.gift}
          <span class="ms-tile__text"><strong>Gift wrap</strong><span>${money(this.wrapPrice)} a bottle</span></span>
          <span class="ms-tile__tick" aria-hidden="true"></span>
        </label>`;
    }

    swatchesHTML(r) {
      if (!this.wrapPrice || !WRAP_COLORS.includes(r.giftWrap)) return '';
      const k = esc(r.key);
      return `
        <div class="ms-swatches" role="radiogroup" aria-label="Gift wrap colour">
          <span class="ms-swatches__label">Wrap colour</span>
          ${WRAP_COLORS.map((c) => `
            <label class="ms-swatch">
              <input type="radio" name="ms-wrap-${k}" data-wrapcolor="${k}" value="${c}"${r.giftWrap === c ? ' checked' : ''}>
              <span class="ms-swatch__dot ms-swatch__dot--${c.toLowerCase()}" aria-hidden="true"></span>${c}
            </label>`).join('')}
        </div>`;
    }

    quoteFor(r) {
      return this.quote?.recipients?.find((q) => q.key === r.key) || null;
    }

    quoteHTML(r) {
      const m = missing(r);
      if (m.length) return `<p class="ms-small ms-muted">Add ${esc(listJoin(m))} to see shipping.</p>`;
      const q = this.quoteFor(r);
      if (!q) {
        const ready = this.state.recipients.filter(complete).length;
        return `<p class="ms-small ms-muted">${ready < 2 ? 'Finish a second recipient to see shipping.' : this.loading ? 'Pricing\u2026' : 'Shipping will show here.'}</p>`;
      }
      if (q.error) return `<p class="ms-err">${esc(q.error)}</p>`;
      const rates = (q.rates || []).length > 1
        ? `<fieldset class="ms-rates"><legend class="label">Shipping speed</legend>
            ${q.rates.map((rate) => `
              <label class="ms-rate">
                <input type="radio" name="ms-rate-${esc(r.key)}" data-rate="${esc(r.key)}" value="${esc(rate.title)}"${q.rate?.title === rate.title ? ' checked' : ''}>
                <span>${esc(rate.title)}</span><span>${rate.priceCents ? money(rate.priceCents) : 'Free'}</span>
              </label>`).join('')}
          </fieldset>`
        : `<p class="ms-small">Shipping: ${esc(q.rate?.title || 'Standard')}</p>`;
      const wrap = (q.lines || []).find((l) => l.wrapColor);
      const wrapCents = wrap ? wrap.quantity * wrap.unitPriceCents : 0;
      return `${rates}
        <dl class="ms-totals ms-totals--card">
          <div><dt>Bottles</dt><dd>${money(q.subtotalCents - wrapCents)}</dd></div>
          ${wrap ? `<div><dt>Gift wrap <span class="ms-muted">(${esc(wrap.wrapColor)}, ${wrap.quantity})</span></dt><dd>${money(wrapCents)}</dd></div>` : ''}
          ${q.discountCents ? `<div><dt>Discount</dt><dd>${money(-q.discountCents)}</dd></div>` : ''}
          <div><dt>Shipping</dt><dd>${q.shippingCents ? money(q.shippingCents) : 'Free'}</dd></div>
          ${q.taxCents ? `<div><dt>Tax</dt><dd>${money(q.taxCents)}</dd></div>` : ''}
          <div class="ms-totals__grand"><dt>This recipient</dt><dd>${money(q.totalCents)}</dd></div>
        </dl>`;
    }

    noticeHTML() {
      return this.notice ? `<div class="ms-panel ms-panel--note">${this.notice}</div>` : '';
    }

    summaryHTML() {
      const s = this.state;
      const n = s.recipients.length;
      const ready = s.recipients.filter(complete).length;
      const bottles = s.recipients.reduce((a, r) => a + units(r), 0);
      const assigned = this.assignedByVariant();
      const unassigned = s.items.reduce((a, it) => a + Math.max(0, it.cartQty - (assigned.get(it.variantId) || 0)), 0);
      const q = this.quote;
      const lines = [];
      lines.push(`<div><dt>Recipients</dt><dd>${n}${ready < n ? ` <span class="ms-muted">(${ready} ready)</span>` : ''}</dd></div>`);
      lines.push(`<div><dt>Bottles</dt><dd>${bottles}</dd></div>`);
      if (q && ready >= 2) {
        const t = q.totals;
        let wrapCents = 0;
        let wraps = 0;
        for (const rq of q.recipients || []) {
          for (const l of rq.lines || []) if (l.wrapColor) { wrapCents += l.quantity * l.unitPriceCents; wraps += l.quantity; }
        }
        lines.push(`<div><dt>Subtotal</dt><dd>${money(t.subtotalCents - wrapCents)}</dd></div>`);
        if (wrapCents) lines.push(`<div><dt>Gift wrap <span class="ms-muted">(${wraps})</span></dt><dd>${money(wrapCents)}</dd></div>`);
        if (t.discountCents) lines.push(`<div><dt>Discount${q.discountTitle ? ` <span class="ms-muted">(${esc(q.discountTitle)})</span>` : ''}</dt><dd>${money(-t.discountCents)}</dd></div>`);
        lines.push(`<div><dt>Shipping</dt><dd>${t.shippingCents ? money(t.shippingCents) : 'Free'}</dd></div>`);
        if (t.taxCents) lines.push(`<div><dt>Tax</dt><dd>${money(t.taxCents)}</dd></div>`);
        lines.push(`<div class="ms-totals__grand"><dt>Total${ready < n ? ' so far' : ''}</dt><dd>${money(t.totalCents)}</dd></div>`);
      }
      const notes = [];
      if (this.loading) notes.push('<p class="ms-small ms-muted">Updating prices\u2026</p>');
      if (this.quoteError) notes.push(`<p class="ms-err">${esc(this.quoteError)}</p>`);
      for (const w of q?.warnings || []) notes.push(`<p class="ms-err">${esc(w)}</p>`);
      if (q?.freeShippingCode) {
        const who = q.freeShippingKey ? this.state.recipients.find((r) => r.key === q.freeShippingKey) : null;
        const name = who ? `${who.firstName} ${who.lastName}`.trim() : '';
        notes.push(`<p class="ms-ok ms-small">${q.freeShippingKey ? `Free-shipping code covers ${name ? esc(name) : 'one recipient'}'s shipping (one shipment, like a normal order).` : 'Free-shipping code applied.'}</p>`);
      }
      if (unassigned) notes.push(`<p class="ms-small ms-warn">${plural(unassigned, 'bottle')} from your cart ${unassigned === 1 ? "isn't" : "aren't"} assigned to anyone and won't be ordered.</p>`);
      return `<dl class="ms-totals">${lines.join('')}</dl>${notes.join('')}`;
    }

    blockers() {
      const s = this.state;
      const out = [];
      if (s.recipients.length < 2) out.push('Add at least two recipients. For one address, use the normal checkout.');
      s.recipients.forEach((r, i) => {
        const m = missing(r);
        if (m.length) out.push(`Recipient ${i + 1} needs ${listJoin(m)}.`);
      });
      if (out.length) return out;
      if (!this.quote || this.quoteSig !== this.requestSig()) return [this.loading ? 'Updating prices\u2026' : 'Waiting for prices\u2026'];
      if (!this.quote.ok) {
        const bad = this.quote.recipients.filter((q) => q.error).length;
        out.push(bad ? `Fix ${plural(bad, 'recipient')} marked in red.` : 'Fix the issues listed above.');
      }
      if (!emailOk(s.email)) out.push('Enter your email for the receipt.');
      return out;
    }

    payHTML() {
      const b = this.blockers();
      if (this.paying) return '<button type="button" class="btn btn--primary w-full ms-pay is-loading" disabled>Starting checkout\u2026</button>';
      const total = this.quote && b.length === 0 ? ` \u00b7 ${money(this.quote.totals.totalCents)}` : '';
      return `
        <button type="button" class="btn btn--primary w-full ms-pay" data-act="pay"${b.length ? ' disabled' : ''}>Continue to payment${total}</button>
        ${this.payError ? `<p class="ms-err" role="alert">${this.payError}</p>` : ''}
        ${b.length ? `<ul class="ms-blockers ms-small">${b.slice(0, 4).map((x) => `<li>${esc(x)}</li>`).join('')}${b.length > 4 ? `<li>\u2026and ${b.length - 4} more</li>` : ''}</ul>` : ''}`;
    }

    barHTML() {
      const b = this.paying ? ['busy'] : this.blockers();
      const label = this.quote && this.state.recipients.filter(complete).length >= 2 ? money(this.quote.totals.totalCents) : plural(this.state.recipients.length, 'recipient');
      return `<span class="ms-bar__total">${label}</span>
        <button type="button" class="btn btn--primary btn--narrow" data-act="${b.length ? 'to-summary' : 'pay'}">${b.length ? 'Review' : 'Continue to payment'}</button>`;
    }

    refresh(...regions) {
      const want = new Set(regions.length ? regions : ['pool', 'summary', 'pay', 'bar', 'quotes', 'count']);
      const set = (name, html) => {
        const el = this.querySelector(`[data-region="${name}"]`);
        if (el) el.innerHTML = html;
      };
      if (want.has('pool')) set('pool', this.poolHTML());
      if (want.has('summary')) set('summary', this.summaryHTML());
      if (want.has('pay')) set('pay', this.payHTML());
      if (want.has('bar')) set('bar', this.barHTML());
      if (want.has('notice')) set('notice', this.noticeHTML());
      if (want.has('count')) set('count', `${this.state.recipients.length}`);
      if (want.has('quotes')) {
        for (const r of this.state.recipients) {
          const el = this.querySelector(`[data-quote="${esc(r.key)}"]`);
          if (el) el.innerHTML = this.quoteHTML(r);
          const badge = this.querySelector(`[data-badge="${esc(r.key)}"]`);
          if (badge) badge.outerHTML = this.badgeHTML(r, this.state.recipients.indexOf(r));
          const row = this.querySelector(`[data-row="${esc(r.key)}"]`);
          if (row) row.outerHTML = this.rowHTML(r, this.state.recipients.indexOf(r));
        }
      }
    }

    // -- events -------------------------------------------------------------
    bind() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('input', (e) => this.onInput(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('submit', (e) => e.preventDefault());
      let raf = 0;
      const sync = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          this.syncBar();
        });
      };
      window.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      // Keep focus in the street field while a suggestion is clicked or tapped; close the list once focus leaves.
      this.addEventListener('mousedown', (e) => {
        if (e.target.closest('.ms-ac')) e.preventDefault();
      });
      this.addEventListener('focusout', (e) => {
        if (this.ac && e.target === this.ac.input) setTimeout(() => this.ac && document.activeElement !== this.ac.input && this.closeSuggest(), 180);
      });
      this.addEventListener('keydown', (e) => {
        if (this.ac && e.target === this.ac.input) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            return this.moveSuggest(e.key === 'ArrowDown' ? 1 : -1);
          }
          if (e.key === 'Enter' && this.ac.active >= 0) {
            e.preventDefault();
            return this.pickSuggest(this.ac.active);
          }
          if (e.key === 'Escape' || e.key === 'Tab') this.closeSuggest();
        }
        if (e.key === 'Enter' && e.target.matches('[data-g="code"]')) {
          e.preventDefault();
          this.applyCode();
        }
      });
    }

    recipient(key) {
      return this.state.recipients.find((r) => r.key === key);
    }

    onInput(e) {
      const t = e.target;
      if (t.dataset.f && t.dataset.k) {
        const r = this.recipient(t.dataset.k);
        if (!r) return;
        r[t.dataset.f] = t.value;
        if (t.dataset.f === 'address1') this.suggest(t, e);
        if (t.dataset.f === 'giftMessage') {
          const c = this.querySelector(`[data-count="${esc(r.key)}"]`);
          if (c) c.textContent = `${t.value.length}/400`;
        }
        if (t.dataset.f === 'firstName' || t.dataset.f === 'lastName') {
          const h = this.querySelector(`[data-title="${esc(r.key)}"]`);
          if (h) h.outerHTML = this.titleHTML(r);
        }
        if (t.dataset.f === 'giftTo' || t.dataset.f === 'giftFrom' || t.dataset.f === 'giftMessage') {
          const st = this.querySelector(`[data-notestate="${esc(r.key)}"]`);
          if (st) st.textContent = r.giftTo || r.giftFrom || r.giftMessage ? 'Added' : 'Free';
        }
        this.save();
        if (t.dataset.f === 'zip' && /^\d{5}$/.test(t.value.trim())) this.scheduleQuote(400);
      } else if (t.dataset.g === 'email') {
        this.state.email = t.value.trim();
        this.save();
        this.refresh('pay', 'bar');
      } else if (t.dataset.g === 'code') {
        this.state.code = t.value.trim();
        this.save();
      }
    }

    onChange(e) {
      const t = e.target;
      if (t.dataset.f && t.dataset.k) {
        const r = this.recipient(t.dataset.k);
        if (!r) return;
        r[t.dataset.f] = t.value;
        if (t.dataset.f === 'zip') r.zip = cleanZip(t.value) || t.value.trim();
        if (t.dataset.f === 'zip' && t.value !== r.zip) t.value = r.zip;
        this.save();
        this.refresh('quotes', 'pay', 'bar');
        if (t.dataset.f !== 'giftMessage' && t.dataset.f !== 'giftTo' && t.dataset.f !== 'giftFrom') this.scheduleQuote(250);
        else this.scheduleQuote(1500);
      } else if (t.matches('[data-qty]')) {
        this.setQty(t.dataset.k, t.dataset.v, parseInt(t.value, 10) || 0);
      } else if (t.matches('[data-wrap]')) {
        const r = this.recipient(t.dataset.wrap);
        if (r) {
          r.giftWrap = t.checked ? (WRAP_COLORS.includes(r.lastWrap) ? r.lastWrap : WRAP_COLORS[0]) : '';
          if (r.giftWrap) r.lastWrap = r.giftWrap;
          this.save();
          t.closest('.ms-tile')?.classList.toggle('is-on', !!r.giftWrap);
          const sw = this.querySelector(`[data-swatches="${esc(r.key)}"]`);
          if (sw) sw.innerHTML = this.swatchesHTML(r);
          this.scheduleQuote(0);
        }
      } else if (t.matches('[data-wrapcolor]')) {
        const r = this.recipient(t.dataset.wrapcolor);
        if (r && WRAP_COLORS.includes(t.value)) {
          r.giftWrap = t.value;
          r.lastWrap = t.value;
          this.save();
          this.scheduleQuote(0);
        }
      } else if (t.matches('[data-rate]')) {
        const r = this.recipient(t.dataset.rate);
        if (r) {
          r.rateTitle = t.value;
          this.save();
          this.scheduleQuote(0);
        }
      } else if (t.matches('[data-saved]')) {
        const a = this.customer?.addresses?.[Number(t.value)];
        const r = this.recipient(t.dataset.saved);
        if (a && r) {
          for (const f of ADDRESS_FIELDS) r[f] = String(a[f] ?? '');
          r.zip = cleanZip(r.zip);
          this.save();
          this.replaceCard(r);
          this.scheduleQuote(0);
        }
      } else if (t.matches('[data-csv]')) {
        const file = t.files?.[0];
        t.value = '';
        if (file) this.importCSV(file);
      }
    }

    onClick(e) {
      const opt = e.target.closest('[data-ac]');
      if (opt && this.ac) return this.pickSuggest(Number(opt.dataset.ac));
      const btn = e.target.closest('[data-act]');
      if (!btn || !this.contains(btn)) return;
      const act = btn.dataset.act;
      const k = btn.dataset.k;
      const v = btn.dataset.v;
      if (act === 'inc') this.setQty(k, v, (this.recipient(k)?.items[v] || 0) + 1);
      else if (act === 'dec') this.setQty(k, v, (this.recipient(k)?.items[v] || 0) - 1);
      else if (act === 'add') this.addRecipient();
      else if (act === 'remove') this.removeRecipient(k);
      else if (act === 'expand') {
        this.expanded = k;
        this.render();
        this.querySelector(`[data-card="${k}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } else if (act === 'collapse') {
        this.expanded = null;
        this.render();
        this.querySelector(`[data-row="${k}"]`)?.scrollIntoView({ block: 'center' });
      } else if (act === 'note') {
        const r = this.recipient(k);
        const box = this.querySelector(`[data-notebox="${esc(k)}"]`);
        if (r && box) {
          const open = box.hidden;
          box.hidden = !open;
          r.noteOpen = open;
          btn.setAttribute('aria-expanded', String(open));
          btn.classList.toggle('is-on', open || !!(r.giftTo || r.giftFrom || r.giftMessage));
          this.save();
          if (open) box.querySelector('[data-f="giftTo"]')?.focus();
        }
      } else if (act === 'all1') this.oneToEach(v);
      else if (act === 'csv') this.querySelector('[data-csv]')?.click();
      else if (act === 'template') this.downloadTemplate();
      else if (act === 'apply-code') this.applyCode();
      else if (act === 'pay') this.pay();
      else if (act === 'to-summary') this.querySelector('.ms-summary')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setQty(key, variantId, qty) {
      const r = this.recipient(key);
      if (!r) return;
      const q = Math.max(0, Math.min(MAX_QTY, qty));
      const kinds = Object.keys(r.items).filter((id) => r.items[id] > 0 && id !== variantId).length;
      if (q > 0 && kinds >= MAX_ITEMS) return;
      if (q) r.items[variantId] = q;
      else delete r.items[variantId];
      this.save();
      const box = this.querySelector(`[data-items="${key}"]`);
      if (box) box.innerHTML = this.itemsHTML(r);
      this.refresh('pool', 'quotes', 'summary', 'pay', 'bar');
      this.scheduleQuote(600);
    }

    // -- street suggestions -------------------------------------------------
    suggest(input, e) {
      clearTimeout(this.acTimer);
      // Only typing asks. Browser and iPhone autofill set the whole address with a plain Event, or an InputEvent
      // with no inputType or "insertReplacementText" (Safari), and need no list on top.
      const q = input.value.trim();
      const typed = e instanceof InputEvent && e.inputType && e.inputType !== 'insertReplacementText';
      if (!this.acToken || !typed || q.length < 4 || !/\d/.test(q)) return this.closeSuggest();
      this.acTimer = setTimeout(() => this.fetchSuggest(input, q), 200);
    }

    async fetchSuggest(input, q) {
      this.acCtrl?.abort();
      const ctrl = (this.acCtrl = new AbortController());
      const k = input.dataset.k;
      // One Mapbox session per address being typed; picking a suggestion ends it.
      if (this.acSession?.k !== k) this.acSession = { k, id: sessionId() };
      let data;
      try {
        const qs = new URLSearchParams({ access_token: this.acToken, session_token: this.acSession.id, language: 'en', country: 'us', types: 'address', proximity: 'ip', limit: '5' });
        const res = await fetch(`${AUTOFILL}/suggest/${encodeURIComponent(q)}?${qs}`, { signal: ctrl.signal });
        if (!res.ok) {
          if ([401, 402, 403, 429].includes(res.status)) this.acToken = ''; // bad token or free tier used up: plain typing from here
          return this.closeSuggest();
        }
        data = await res.json();
      } catch {
        return; // aborted by a newer keystroke, or offline
      }
      if (document.activeElement !== input || input.value.trim() !== q) return;
      const items = (data.suggestions || []).filter((s) => s.address_line1 && STATE_CODES.has(stateOf(s))).slice(0, 5);
      if (!items.length) return this.closeSuggest();
      this.ac = { input, items, active: -1 };
      const id = `ms-ac-${esc(k)}`;
      const html = `
        <div class="ms-ac" id="${id}">
          <ul class="ms-ac__list" role="listbox" aria-label="Address suggestions">
            ${items.map((s, i) => `
              <li class="ms-ac__opt" role="option" id="${id}-${i}" data-ac="${i}" aria-selected="false">
                <span class="ms-ac__line1">${esc(s.address_line1)}</span>
                <span class="ms-ac__line2">${esc([this.addressFrom(s).city, `${stateOf(s)} ${s.postcode || ''}`.trim()].filter(Boolean).join(', '))}</span>
              </li>`).join('')}
          </ul>
          <p class="ms-ac__by">Powered by Mapbox</p>
        </div>`;
      const old = input.parentElement.querySelector('.ms-ac');
      if (old) old.outerHTML = html;
      else input.insertAdjacentHTML('afterend', html);
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
    }

    moveSuggest(step) {
      const { input, items } = this.ac;
      // -1 is "nothing highlighted" (what was typed); the arrows cycle through it, as a browser's own list does.
      const n = items.length + 1;
      this.ac.active = ((this.ac.active + 1 + step + n) % n) - 1;
      const opts = input.parentElement.querySelectorAll('.ms-ac__opt');
      opts.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.ac.active)));
      const on = opts[this.ac.active];
      if (on) {
        input.setAttribute('aria-activedescendant', on.id);
        on.scrollIntoView({ block: 'nearest' });
      } else input.removeAttribute('aria-activedescendant');
    }

    closeSuggest() {
      clearTimeout(this.acTimer);
      this.acCtrl?.abort();
      if (!this.ac) return;
      const { input } = this.ac;
      this.ac = null;
      input.parentElement?.querySelector('.ms-ac')?.remove();
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }

    // A suggestion is a whole address: what it lacks is cleared, never kept from the street it replaces.
    addressFrom(a) {
      const st = stateOf(a);
      return {
        address1: a.address_line1 || '',
        address2: a.address_line2 || '',
        city: a.address_level2 || (typeof a.place === 'string' ? a.place : ''),
        provinceCode: STATE_CODES.has(st) ? st : '',
        zip: cleanZip(a.postcode),
      };
    }

    pickSuggest(i) {
      const s = this.ac?.items[i];
      const input = this.ac?.input;
      const r = input && this.recipient(input.dataset.k);
      const session = this.acSession;
      this.closeSuggest();
      this.acSession = null;
      if (!s || !r) return;
      Object.assign(r, this.addressFrom(s));
      this.save();
      this.replaceCard(r);
      // Next stop is the apartment line, which suggestions never know.
      document.getElementById(`ms-address2-${r.key}`)?.focus();
      this.refresh('quotes', 'pay', 'bar');
      this.scheduleQuote(0);
      if (s.action?.id && session) this.retrieveSuggest(s.action.id, session.id, r, ADDRESS_FIELDS.map((f) => r[f]).join('|'));
    }

    // Retrieve closes Mapbox's billed session. Its record may refine the address, but only one the buyer
    // hasn't touched since the pick; a late answer never overwrites a newer edit.
    async retrieveSuggest(id, session, r, filled) {
      let p = null;
      try {
        const qs = new URLSearchParams({ access_token: this.acToken, session_token: session });
        const res = await fetch(`${AUTOFILL}/retrieve/${encodeURIComponent(id)}?${qs}`, { signal: AbortSignal.timeout ? AbortSignal.timeout(2500) : undefined });
        p = res.ok ? (await res.json()).features?.[0]?.properties : null;
      } catch {
        return;
      }
      if (!p?.address_line1 || !this.state.recipients.includes(r) || ADDRESS_FIELDS.map((f) => r[f]).join('|') !== filled) return;
      const next = this.addressFrom(p);
      const changed = Object.keys(next).filter((f) => next[f] && next[f] !== r[f]);
      if (!changed.length) return;
      for (const f of changed) {
        r[f] = next[f];
        const el = this.querySelector(`[data-k="${esc(r.key)}"][data-f="${f}"]`);
        if (el) el.value = next[f];
      }
      this.save();
      this.refresh('quotes', 'pay', 'bar');
      this.scheduleQuote(0);
    }

    replaceCard(r) {
      const el = this.querySelector(`[data-card="${esc(r.key)}"]`);
      if (el) el.outerHTML = this.cardHTML(r, this.state.recipients.indexOf(r));
    }

    addRecipient() {
      if (this.state.recipients.length >= MAX_RECIPIENTS) {
        this.notice = `That's the most we can take in one order (${MAX_RECIPIENTS}). Email ${esc(this.support)} for bigger lists.`;
        this.refresh('notice');
        return;
      }
      const r = this.newRecipient();
      this.state.recipients.push(r);
      this.save();
      if (this.state.recipients.length > CARD_LIMIT) this.expanded = r.key;
      this.render();
      const card = this.querySelector(`[data-card="${esc(r.key)}"]`);
      card?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      card?.querySelector('[data-f="firstName"]')?.focus({ preventScroll: true });
      this.scheduleQuote(300);
    }

    removeRecipient(key) {
      const r = this.recipient(key);
      if (!r) return;
      if (!isBlank(r) && !window.confirm(`Remove ${`${r.firstName} ${r.lastName}`.trim() || 'this recipient'}?`)) return;
      this.state.recipients = this.state.recipients.filter((x) => x.key !== key);
      if (this.expanded === key) this.expanded = null;
      this.save();
      this.render();
      this.scheduleQuote(0);
    }

    oneToEach(variantId) {
      for (const r of this.state.recipients) {
        const kinds = Object.keys(r.items).filter((id) => r.items[id] > 0 && id !== variantId).length;
        if (kinds < MAX_ITEMS) r.items[variantId] = 1;
      }
      this.save();
      this.render();
      this.scheduleQuote(300);
    }

    assignedByVariant() {
      const m = new Map();
      for (const r of this.state.recipients) {
        for (const [id, q] of Object.entries(r.items)) m.set(id, (m.get(id) || 0) + q);
      }
      return m;
    }

    applyCode() {
      const input = this.querySelector('[data-g="code"]');
      this.state.code = (input?.value || '').trim();
      this.save();
      this.scheduleQuote(0);
    }

    // -- CSV ----------------------------------------------------------------
    downloadTemplate() {
      const items = this.state.items;
      const wrap = this.wrapPrice ? ['gift_wrap'] : [];
      const head = ['first_name', 'last_name', 'company', 'address1', 'address2', 'city', 'state', 'zip', 'phone', 'gift_to', 'gift_from', 'gift_message', ...wrap, ...items.map((i) => `Qty: ${i.title}`)];
      const sample = ['Jane', 'Doe', '', '123 Main St', 'Apt 4', 'Austin', 'TX', '78701', '', 'Jane', 'The Smith Family', 'Happy holidays!', ...(wrap.length ? [WRAP_COLORS[0]] : []), ...items.map((_, j) => (j === 0 ? '1' : ''))];
      const csv = `${[head, sample].map((r) => r.map(csvCell).join(',')).join('\r\n')}\r\n`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      a.download = 'multi-ship-recipients.csv';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(a.href);
        a.remove();
      }, 1000);
    }

    async importCSV(file) {
      if (file.size > 2_000_000) {
        this.notice = '<p class="ms-err">That file is too big. Keep it under 2 MB (about 10,000 rows).</p>';
        this.refresh('notice');
        return;
      }
      const rows = parseCSV(await file.text());
      if (rows.length < 2) {
        this.notice = '<p class="ms-err">We couldn\'t find any recipients in that file. Use the template: one header row, then one row per person.</p>';
        this.refresh('notice');
        return;
      }
      const header = rows[0].map(norm);
      const col = {};
      for (const [f, aliases] of Object.entries(FIELD_ALIASES)) {
        const i = header.findIndex((h) => aliases.includes(h));
        if (i >= 0) col[f] = i;
      }
      const items = this.state.items;
      const itemCols = [];
      header.forEach((h, i) => {
        const hit = items.find((it) => h === `qty${norm(it.title)}` || h === norm(it.title) || (it.sku && h === norm(it.sku)) || (it.sku && h === `qty${norm(it.sku)}`));
        if (hit && !Object.values(col).includes(i)) itemCols.push({ i, variantId: hit.variantId });
      });
      const findItem = (s) => {
        const n = norm(s);
        return n ? items.find((it) => norm(it.sku) === n || norm(it.title) === n || norm(it.title).startsWith(n)) : null;
      };
      const get = (row, f) => (col[f] !== undefined ? String(row[col[f]] ?? '').trim() : '');
      // No gift_wrap column: new people follow the cart's wrap, people already listed keep theirs.
      const wrapCol = col.giftWrap !== undefined;
      // A re-upload only overwrites the optional fields its file actually has columns for.
      const keepIfNoColumn = ['company', 'address2', 'phone', 'giftTo', 'giftFrom', 'giftMessage', 'giftWrap'].filter((f) => col[f] === undefined);

      // Keep only cards the buyer started an address on. Starter cards on a one-bottle cart
      // come pre-filled with a bottle (newRecipient) and would otherwise stay as empty rows.
      const keep = this.state.recipients.filter((r) => ADDRESS_FIELDS.some((f) => String(r[f] || '').trim()));
      // Same person + address = same recipient: a re-uploaded (edited) list updates them, and
      // a person listed twice in one file gets the quantities combined.
      const sig = (r) => norm(`${r.firstName}${r.lastName}${r.address1}${r.address2}${r.zip}`);
      const bySig = new Map(keep.map((r) => [sig(r), r]));
      const added = [];
      const issues = [];
      let noItems = 0;
      let updated = 0;
      let combined = 0;
      for (let ri = 1; ri < rows.length; ri++) {
        const row = rows[ri];
        const r = blankRecipient();
        r.firstName = get(row, 'firstName');
        r.lastName = get(row, 'lastName');
        if (!r.firstName && !r.lastName && get(row, 'name')) {
          const full = get(row, 'name').replace(/\s+/g, ' ');
          const cut = full.lastIndexOf(' ');
          r.firstName = cut > 0 ? full.slice(0, cut) : full;
          r.lastName = cut > 0 ? full.slice(cut + 1) : '';
        }
        r.company = get(row, 'company');
        r.address1 = get(row, 'address1');
        r.address2 = get(row, 'address2');
        r.city = get(row, 'city');
        r.provinceCode = cleanState(get(row, 'provinceCode'));
        r.zip = cleanZip(get(row, 'zip'));
        r.phone = get(row, 'phone');
        r.giftTo = get(row, 'giftTo').slice(0, 60);
        r.giftFrom = get(row, 'giftFrom').slice(0, 60);
        r.giftMessage = get(row, 'giftMessage').slice(0, 400);
        if (this.wrapPrice) r.giftWrap = wrapCol ? wrapOf(get(row, 'giftWrap')) : this.cartWrap;
        if (itemCols.length) {
          for (const c of itemCols) {
            const q = qtyOf(row[c.i]);
            if (q > 0) r.items[c.variantId] = Math.min(MAX_QTY, (r.items[c.variantId] || 0) + q);
          }
        } else {
          const q = qtyOf(get(row, 'quantity'));
          const sku = get(row, 'sku');
          const it = sku ? findItem(sku) : items.length === 1 ? items[0] : null;
          if (it) r.items[it.variantId] = Math.min(MAX_QTY, q > 0 ? q : 1);
        }
        if (isBlank(r)) continue;
        if (!units(r)) noItems++;
        const m = missing(r).filter((x) => x !== 'a bottle');
        if (m.length && issues.length < 6) issues.push(`Row ${ri + 1}: missing ${listJoin(m)}`);
        const k = sig(r);
        const prev = k ? bySig.get(k) : null;
        if (prev && added.includes(prev)) {
          for (const [v, q] of Object.entries(r.items)) prev.items[v] = Math.min(MAX_QTY, (prev.items[v] || 0) + q);
          // One row per bottle is common: take the note and wrap from whichever row has them.
          for (const f of ['giftTo', 'giftFrom', 'giftMessage', 'giftWrap']) if (!prev[f] && r[f]) prev[f] = r[f];
          combined++;
          continue;
        }
        if (prev) {
          Object.assign(prev, { ...r, key: prev.key, ...Object.fromEntries(keepIfNoColumn.map((f) => [f, prev[f]])) });
          updated++;
          continue;
        }
        if (k) bySig.set(k, r);
        added.push(r);
      }

      const room = MAX_RECIPIENTS - keep.length;
      const take = added.slice(0, Math.max(0, room));
      this.state.recipients = [...keep, ...take];
      while (this.state.recipients.length < 2) this.state.recipients.push(blankRecipient());
      this.expanded = null;
      this.save();

      const done = [];
      if (take.length || !updated) done.push(`Added ${plural(take.length, 'recipient')}`);
      if (updated) done.push(`updated ${plural(updated, 'recipient')} already on your list`);
      const parts = [`<p><strong>${listJoin(done).replace(/^u/, 'U')} from ${esc(file.name)}.</strong>${combined ? ` ${plural(combined, 'repeated row')} combined.` : ''}</p>`];
      if (added.length > take.length) parts.push(`<p class="ms-err">Only the first ${take.length} fit (limit ${MAX_RECIPIENTS} per order). Email ${esc(this.support)} for bigger lists.</p>`);
      if (issues.length) parts.push(`<p class="ms-warn">Some rows need fixing below:</p><ul class="ms-small">${issues.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>`);
      if (noItems) {
        parts.push(items.length > 1
          ? `<p class="ms-warn">${plural(noItems, 'row')} had no bottle quantities. Use "1 to each" above, or add a "Qty: bottle name" column like the template.</p>`
          : `<p class="ms-warn">${plural(noItems, 'row')} had no quantity.</p>`);
      }
      this.notice = parts.join('');
      this.render();
      this.scheduleQuote(0);
    }

    // -- pricing ------------------------------------------------------------
    payload(r) {
      const gift = r.giftTo || r.giftFrom || r.giftMessage ? { to: r.giftTo.trim(), from: r.giftFrom.trim(), message: r.giftMessage.trim() } : undefined;
      return {
        key: r.key,
        firstName: r.firstName.trim(),
        lastName: r.lastName.trim(),
        company: r.company.trim(),
        address1: r.address1.trim(),
        address2: r.address2.trim(),
        city: r.city.trim(),
        provinceCode: r.provinceCode,
        zip: r.zip.trim(),
        phone: r.phone.trim(),
        rateTitle: r.rateTitle || '',
        giftMessage: gift,
        ...(this.wrapPrice && WRAP_COLORS.includes(r.giftWrap) ? { giftWrap: r.giftWrap } : {}),
        items: Object.entries(r.items)
          .filter(([, q]) => q > 0)
          .map(([variantId, quantity]) => ({ variantId, quantity })),
      };
    }

    requestBody() {
      return {
        shop: this.shop,
        email: emailOk(this.state.email) ? this.state.email.trim() : '',
        discountCode: this.state.code || '',
        recipients: this.state.recipients.filter(complete).map((r) => this.payload(r)),
      };
    }

    requestSig() {
      const b = this.requestBody();
      // Email changes don't change prices; keep them out so typing it doesn't re-price.
      return JSON.stringify({ ...b, email: '' });
    }

    scheduleQuote(delay) {
      clearTimeout(this._qt);
      this._qt = setTimeout(() => this.runQuote(), delay);
    }

    async runQuote(force = false) {
      const body = this.requestBody();
      const sig = this.requestSig();
      if (body.recipients.length < 2) {
        this.quote = null;
        this.quoteSig = '';
        this.quoteError = '';
        this.refresh('quotes', 'summary', 'pay', 'bar');
        return;
      }
      if (!force && sig === this.quoteSig && this.quote) return;
      const seq = ++this.seq;
      this.loading = true;
      this.refresh('summary', 'pay', 'bar');
      try {
        const res = await fetch(`${this.endpoint}/quote`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const data = await res.json().catch(() => ({}));
        if (seq !== this.seq) return;
        if (res.ok) {
          this.quote = data;
          this.quoteSig = sig;
          this.quoteError = '';
        } else {
          this.quoteError = data.error === 'invalid request' ? this.describeIssues(data.issues, body) : data.error || "We couldn't price this right now. Try again in a moment.";
        }
      } catch {
        if (seq !== this.seq) return;
        this.quoteError = "We couldn't reach our pricing service. Check your connection and try again.";
      } finally {
        if (seq === this.seq) {
          this.loading = false;
          this.refresh('quotes', 'summary', 'pay', 'bar');
        }
      }
    }

    describeIssues(issues, body) {
      const first = (issues || [])[0];
      if (!first) return 'Something in the form needs fixing.';
      const idx = first.path?.[0] === 'recipients' ? first.path[1] : null;
      const r = typeof idx === 'number' ? body.recipients[idx] : null;
      const n = r ? this.state.recipients.findIndex((x) => x.key === r.key) + 1 : 0;
      return n ? `Recipient ${n}: ${first.message}.` : `${first.message}.`;
    }

    async pay() {
      if (this.paying) return;
      const btns = this.querySelectorAll('[data-act="pay"]');
      if (this.quoteSig !== this.requestSig()) {
        await this.runQuote(true);
        this.payError = 'Prices were updated. Check the total, then press Continue again.';
        this.refresh('pay');
        return;
      }
      if (this.blockers().length) {
        this.refresh('pay', 'bar');
        this.querySelector('.ms-summary')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      this.payError = '';
      this.paying = true;
      btns.forEach((b) => {
        b.disabled = true;
        b.classList.add('is-loading');
      });
      const body = { ...this.requestBody(), email: this.state.email.trim(), expectedTotalCents: this.quote.totals.totalCents };
      const sentSig = this.requestSig();
      let data = {};
      let res;
      try {
        res = await fetch(`${this.endpoint}/checkout`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        data = await res.json().catch(() => ({}));
      } catch {
        res = null;
      }
      if (res?.ok && data.invoiceUrl) {
        this.state.redirect = { invoiceUrl: data.invoiceUrl, sessionId: data.sessionId, at: Date.now() };
        this.save();
        // The bottles now live in the multi-ship checkout; leaving them in the cart
        // would invite a second order and a "you left something" email.
        try {
          await fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });
        } catch {
          /* checkout still works */
        }
        window.location.href = data.invoiceUrl;
        return;
      }
      this.paying = false;
      if (data.quote) {
        // Pin the server's quote to the body that produced it, not to whatever the form says now.
        this.quote = data.quote;
        this.quoteSig = sentSig;
      }
      this.payError = esc(data.error || `We couldn't start checkout. Try again, or email ${this.support} and we'll set it up.`);
      this.refresh();
    }
  }

  customElements.define('multi-ship', MultiShip);
})();
